import { useCallback, useEffect, useMemo, useState } from "react";
import { SortableHeader, TableSortResetBar, sortRowsByKey, useTableSort } from "@/components/dataTable";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { DataTableScrollShell } from "@/components/dataTable/DataTableScrollShell";
import { Checkbox } from "@/components/ui/Field";
import { toast } from "@/lib/toast";
import {
  listHeroCustomFieldSchemas,
  saveHeroCustomFieldConfig,
  type HeroCustomFieldConfigItem,
} from "../../lib/settingsApi";

interface FieldRow {
  schemaId: number;
  propertyUuid: string;
  label: string;
  enabled: boolean;
  showOnTimesheet: boolean;
  showOnFolderDefaults: boolean;
}

export function CustomFieldsSection() {
  const [rows, setRows] = useState<FieldRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { sort, toggle, reset } = useTableSort();
  const sortedRows = useMemo(
    () => sortRowsByKey(rows, sort, (row, key) => key === "label" ? row.label : row[key as keyof FieldRow] as boolean),
    [rows, sort],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const schemas = await listHeroCustomFieldSchemas();
      const list: FieldRow[] = [];
      for (const s of schemas) {
        const relates = (s as { relates?: string }).relates;
        if (relates && relates.toLowerCase() !== "project") continue;
        for (const p of s.properties || []) {
          const prop = p as {
            uuid?: string;
            propertyUuid?: string;
            label?: string;
            enabled?: boolean;
            showOnTimesheet?: boolean;
            show_on_timesheet?: boolean;
            showOnFolderDefaults?: boolean;
            show_on_folder_defaults?: boolean;
          };
          list.push({
            schemaId: s.schemaId,
            propertyUuid: prop.propertyUuid || prop.uuid || "",
            label: prop.label || prop.uuid || "Feld",
            enabled: !!prop.enabled,
            showOnTimesheet: !!(prop.showOnTimesheet ?? prop.show_on_timesheet),
            showOnFolderDefaults: !!(prop.showOnFolderDefaults ?? prop.show_on_folder_defaults),
          });
        }
      }
      setRows(list);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Felder konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function updateRow(idx: number, patch: Partial<FieldRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  async function handleSave() {
    setSaving(true);
    const items: HeroCustomFieldConfigItem[] = rows.map((r) => ({
      schemaId: r.schemaId,
      propertyUuid: r.propertyUuid,
      enabled: r.enabled,
      showOnTimesheet: r.showOnTimesheet,
      showOnFolderDefaults: r.showOnFolderDefaults,
    }));
    try {
      await saveHeroCustomFieldConfig(items);
      toast.success("Konfiguration gespeichert.");
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Hero – Eigene Felder"
      description="Sichtbarkeit in Neuprojekt, Stundennachweis und Ordner-Defaults."
      actions={
        <Button type="button" onClick={handleSave} loading={saving}>
          Speichern
        </Button>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      {!loading && (
        <>
        <TableSortResetBar visible={sort != null} onReset={reset} />
        <DataTableScrollShell
          syncKey={`custom-fields|${rows.length}`}
          head={
            <tr>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label="Feld" columnKey="label" sort={sort} onSort={toggle} /></th>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label="Neues Projekt" columnKey="enabled" sort={sort} onSort={toggle} /></th>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label="Stundennachweis" columnKey="showOnTimesheet" sort={sort} onSort={toggle} /></th>
              <th className="eh-data-th px-3 py-2 text-left"><SortableHeader label="Ordner-Defaults" columnKey="showOnFolderDefaults" sort={sort} onSort={toggle} /></th>
            </tr>
          }
        >
          <tbody className="divide-y divide-separator">
              {sortedRows.map((row) => {
                const idx = rows.indexOf(row);
                return (
                <tr key={`${row.schemaId}-${row.propertyUuid}`}>
                  <td className="px-3 py-2">{row.label}</td>
                  <td className="px-3 py-2">
                    <Checkbox
                      label="Neues Projekt"
                      checked={row.enabled}
                      onChange={(e) => updateRow(idx, { enabled: e.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Checkbox
                      label="Stundennachweis"
                      checked={row.showOnTimesheet}
                      onChange={(e) => updateRow(idx, { showOnTimesheet: e.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Checkbox
                      label="Ordner-Defaults"
                      checked={row.showOnFolderDefaults}
                      onChange={(e) => updateRow(idx, { showOnFolderDefaults: e.target.checked })}
                    />
                  </td>
                </tr>
              )})}
            </tbody>
        </DataTableScrollShell>
        </>
      )}
    </Card>
  );
}
